import { useTranslation } from "react-i18next";
import { useSEO } from "@/lib/useSEO";
import { decideRobots, buildCanonical, buildPageMeta } from "@/lib/seoIndex";
import Breadcrumbs from "@/components/Breadcrumbs";

/**
 * Impressum (§5 TMG / §55 RStV — legally mandatory in Germany).
 * Important: the placeholders below MUST be replaced with the real
 * legal-entity details before going live in DE/AT/CH.
 */
export default function ImprintPage() {
  const { t, i18n } = useTranslation();
  const isDE = i18n.language?.startsWith("de");

  const meta = buildPageMeta("imprint");
  useSEO({
    robots: decideRobots({ pageType: "imprint" }),
    title: meta.title,
    description: meta.description,
    canonical: buildCanonical("/impressum"),
  });

  // Legal entity details — David Schindler (private operator, no legal entity, no register entry).
  const entity = {
    name: "David Schindler",
    address1: "Wacholderweg 3",
    address2: "93197 Zeitlarn",
    country: "Deutschland",
    email: "info@doing-good.org",
    phone: "+49 1575 6279792",
  };

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-8 py-14" data-testid="imprint-page">
      <Breadcrumbs crumbs={[{ label: isDE ? "Impressum" : "Imprint" }]} />
      <h1 className="mt-4 text-4xl sm:text-5xl font-black tracking-tight">
        {isDE ? "Impressum" : "Imprint"}
      </h1>

      {isDE ? (
        <div className="prose-clay mt-10 space-y-8 text-[#3A463C] leading-relaxed">
          <section>
            <h2 className="text-xl font-bold text-[#1F2A24]">Angaben gemäß § 5 TMG</h2>
            <p className="mt-3">
              {entity.name}<br />
              {entity.address1}<br />
              {entity.address2}<br />
              {entity.country}
            </p>
          </section>
          <section>
            <h2 className="text-xl font-bold text-[#1F2A24]">Kontakt</h2>
            <p className="mt-3">
              Telefon: <a className="underline hover:text-[#5B8C66]" href={`tel:${entity.phone.replace(/\s/g, "")}`}>{entity.phone}</a><br />
              E-Mail: <a className="underline hover:text-[#5B8C66]" href={`mailto:${entity.email}`}>{entity.email}</a>
            </p>
          </section>
          <section>
            <h2 className="text-xl font-bold text-[#1F2A24]">Redaktionell verantwortlich</h2>
            <p className="mt-3">
              {entity.name}<br />
              {entity.address1}, {entity.address2}
            </p>
          </section>
          <section>
            <h2 className="text-xl font-bold text-[#1F2A24]">Haftung für Inhalte</h2>
            <p className="mt-3">
              doing-good.org ist eine offene Plattform, die Angebote von Dritten (gemeinnützigen Organisationen, NGOs, Plattformen wie betterplace.org und vostel.de) aggregiert und auf deren Websites verlinkt. Inhalte und Aktualität dieser verlinkten externen Seiten liegen ausschließlich in der Verantwortung der jeweiligen Betreiber. Wir prüfen die Quellen sorgfältig, übernehmen aber keine Gewähr für Vollständigkeit oder Richtigkeit der gelisteten Angebote.
            </p>
            <p className="mt-3">
              Bei Bekanntwerden von Rechtsverletzungen werden wir derartige Inhalte umgehend entfernen. Bitte kontaktieren Sie uns unter <a className="underline hover:text-[#5B8C66]" href={`mailto:${entity.email}`}>{entity.email}</a>.
            </p>
          </section>
          <section>
            <h2 className="text-xl font-bold text-[#1F2A24]">Haftung für Links</h2>
            <p className="mt-3">
              Unser Angebot enthält Links zu externen Websites Dritter, auf deren Inhalte wir keinen Einfluss haben. Deshalb können wir für diese fremden Inhalte auch keine Gewähr übernehmen. Für die Inhalte der verlinkten Seiten ist stets der jeweilige Anbieter oder Betreiber der Seiten verantwortlich.
            </p>
          </section>
          <section>
            <h2 className="text-xl font-bold text-[#1F2A24]">Urheberrecht</h2>
            <p className="mt-3">
              Die durch die Seitenbetreiber erstellten Inhalte und Werke auf diesen Seiten unterliegen dem deutschen Urheberrecht. Beiträge Dritter sind als solche gekennzeichnet. Die Vervielfältigung, Bearbeitung, Verbreitung und jede Art der Verwertung außerhalb der Grenzen des Urheberrechtes bedürfen der schriftlichen Zustimmung des jeweiligen Autors bzw. Erstellers.
            </p>
          </section>
          <section>
            <h2 className="text-xl font-bold text-[#1F2A24]">EU-Streitschlichtung</h2>
            <p className="mt-3">
              Die Europäische Kommission stellt eine Plattform zur Online-Streitbeilegung (OS) bereit: <a className="underline hover:text-[#5B8C66]" href="https://ec.europa.eu/consumers/odr/" target="_blank" rel="noreferrer noopener">https://ec.europa.eu/consumers/odr/</a>. Wir sind nicht bereit oder verpflichtet, an Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle teilzunehmen.
            </p>
          </section>
        </div>
      ) : (
        <div className="prose-clay mt-10 space-y-8 text-[#3A463C] leading-relaxed">
          <section>
            <h2 className="text-xl font-bold text-[#1F2A24]">Provider information per §5 TMG (German law)</h2>
            <p className="mt-3">
              {entity.name}<br />
              {entity.address1}<br />
              {entity.address2}<br />
              {entity.country}
            </p>
          </section>
          <section>
            <h2 className="text-xl font-bold text-[#1F2A24]">Contact</h2>
            <p className="mt-3">
              Phone: <a className="underline hover:text-[#5B8C66]" href={`tel:${entity.phone.replace(/\s/g, "")}`}>{entity.phone}</a><br />
              Email: <a className="underline hover:text-[#5B8C66]" href={`mailto:${entity.email}`}>{entity.email}</a>
            </p>
          </section>
          <section>
            <h2 className="text-xl font-bold text-[#1F2A24]">Liability for content</h2>
            <p className="mt-3">
              doing-good.org is an open aggregator that lists offers from third-party non-profit organisations and platforms (betterplace.org, vostel.de) and links out to their own websites. The content and timeliness of those linked external sites is the sole responsibility of their respective operators. We curate sources carefully but cannot guarantee completeness or accuracy of every listed offer.
            </p>
            <p className="mt-3">
              We will remove any infringing content as soon as we become aware of it. Please reach out to <a className="underline hover:text-[#5B8C66]" href={`mailto:${entity.email}`}>{entity.email}</a>.
            </p>
          </section>
          <section>
            <h2 className="text-xl font-bold text-[#1F2A24]">EU online dispute resolution</h2>
            <p className="mt-3">
              The European Commission provides an online dispute resolution platform: <a className="underline hover:text-[#5B8C66]" href="https://ec.europa.eu/consumers/odr/" target="_blank" rel="noreferrer noopener">https://ec.europa.eu/consumers/odr/</a>.
            </p>
          </section>
        </div>
      )}
    </div>
  );
}
